import { tap, map, finalize, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Payment } from '../../model/payment';
import { Money } from '../../../core/model/money';
import { LocalDate } from '../../../core/model/local-date';
import { Invoice } from '../../model/invoice';
import { RegisterPaymentResultModel, RegisterPaymentViewModel } from '../../model/register-payment-view-model';

@Injectable()
export class PaymentService extends DataService<Payment> {
  constructor(
    private httpClient: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(httpClient, config, messageService, Payment, progressBar, 'payment');
  }

  getPayments(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('payment', file, file.name);
    this.progressBar.start();
    return this.httpClient.post<Payment[]>(`${this.endpoint}/payments`, formData).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getPaymentsByFilter(filter: any): Observable<Payment[]> {
    this.progressBar.start();
    return this.httpClient.post<Payment[]>(`${this.endpoint}`, JSON.stringify(filter)).pipe(
      map(items => items.map(item => this.createModel(item))),
      finalize(() => this.progressBar.stop())
    );
  }

  import(importId: string, payments: Payment[]): Observable<any> {
    this.progressBar.start();
    return this.httpClient.post(`${this.endpoint}/import`, JSON.stringify({ import: importId, payments: payments })).pipe(
      tap(() => this.notification.showSuccess('SUCCESS.PAYMENT_IMPORTED')),
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getRegisterPaymentViewModel(invoiceId: string): Observable<RegisterPaymentViewModel> {
    return this.httpClient
      .get<RegisterPaymentViewModel>(`${this.endpoint}/register-payment-model/${invoiceId}`)
      .pipe(map(result => Object.assign(new RegisterPaymentViewModel(), result)));
  }

  registerPayment(form: any): Observable<RegisterPaymentResultModel> {
    this.progressBar.start();
    return this.httpClient
      .post<RegisterPaymentResultModel>(`${this.endpoint}/register-payment`, JSON.stringify(form), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw error;
        }),
        tap(() => this.notification.showSuccess('SUCCESS.PAYMENT_REGISTERED')),
        finalize(() => this.progressBar.stop())
      );
  }

  regulateFromBalance(invoiceId: string, amount: Money, accountingDate: LocalDate): Observable<any> {
    this.progressBar.start();
    return this.httpClient
      .post(
        `${this.endpoint}/pay-from-balance`,
        { amount: amount, accountingDate: accountingDate, invoiceId: invoiceId },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          throw error;
        }),
        tap(() => this.notification.showSuccess('SUCCESS.PAYMENT_REGISTERED')),
        finalize(() => this.progressBar.stop())
      );
  }

  moveToBalance(model: { contactId: string; invoiceId: string; amount: Money; verificationDescription: string }): Observable<any> {
    return this.httpClient.post(`${this.endpoint}/move-to-balance`, model, { headers: { 'Content-Type': 'application/json' } }).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      tap(() => this.notification.showSuccess('SUCCESS.PAYMENT_MOVED_TO_BALANCE')),
      finalize(() => this.progressBar.stop())
    );
  }
}
